/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";

import { pushOneTeam } from "../../../../middlewares/sysNavigation/SysNavigation";
import { useTeamContext } from "../../../../context/team/TeamContext";
import { Modal, Search, Button, Pagination } from "../../component/Component";
import { _PAGE_SIZE_ } from "../../../../middlewares/other/tools";

const addRoute = "/team/add";
const editRoute = "/team/edit";

const Overview = () => {
  const [teamList, setTeamList] = useState([]);
  const { team, fetchTeams, count } = useTeamContext();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(_PAGE_SIZE_);

  useEffect(() => {
    if (!team) fetchTeams(page, pageSize);
    else setTeamList(team);
  }, [team]);

  const SearchList = (e) => {
    const key = e.target.value.toString().toLowerCase();
    const searchArray = [];
    if (team !== null && team !== undefined) {
      team.map((_team_) => {
        if (
          _team_.firstname.toString().toLowerCase().includes(key) ||
          _team_.lastname.toString().toLowerCase().includes(key) ||
          _team_.email_address.toString().toLowerCase().includes(key) ||
          _team_.phone_number.toString().toLowerCase().includes(key)
        ) {
          return searchArray.push(_team_);
        }
        return true;
      });
    }
    setTeamList(key === "" ? team : searchArray);
  };

  const totalActive = useMemo(
    () => (Array.isArray(teamList) ? teamList.filter((t) => t.status === 1).length : 0),
    [teamList]
  );

  return (
    <div className="h-full overflow-y-auto bg-gray-50 p-4 rounded-b-xl">
      <div className="mx-auto max-w-6xl space-y-5">
        {/* Header / Intro */}
        <div className="relative">
          <div className="pointer-events-none absolute -inset-x-6 -top-6 h-24 rounded-3xl bg-gradient-to-r from-emerald-400/15 via-amber-400/15 to-fuchsia-400/15 blur-2xl -z-10" />
          <div className="relative rounded-2xl border border-gray-200 bg-white/90 shadow-sm backdrop-blur">
            <div className="flex flex-col gap-4 p-5 md:flex-row md:items-center md:justify-between">
              <div>
                <h1 className="text-base font-semibold tracking-tight text-gray-900">
                  Team Directory
                </h1>
                <p className="mt-0.5 text-xs text-gray-600">
                  Manage members, update details, and control access.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <StatChip label="Total" value={count || 0} />
                <StatChip label="Active" value={totalActive} tone="emerald" />
                <Link to={addRoute}>
                  <Button
                    props={{
                      type: "button",
                      title: "Add Team",
                      category: "default",
                      action: "add",
                      onClick: null,
                    }}
                  />
                </Link>
              </div>
            </div>

            {/* Toolbar (Search + subtle divider) */}
            <div className="border-t border-gray-100 px-5 py-3">
              <div className="flex items-center">
                <div className="w-full md:w-96">
                  <Search
                    props={{
                      title: "Search team…",
                      onChange: (e) => SearchList(e),
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Table Card */}
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="relative overflow-hidden rounded-2xl">
            <div className="relative max-h-[65vh] overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 z-10 bg-white/95 backdrop-blur">
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                    <Th>Name</Th>
                    <Th>Phone</Th>
                    <Th>Email</Th>
                    <Th className="text-center">Status</Th>
                    <Th className="text-center">Action</Th>
                  </tr>
                </thead>

                <tbody>
                  {Array.isArray(teamList) && teamList.length > 0 ? (
                    teamList.map((_team_) => (
                      <tr
                        key={_team_.team_id}
                        className="group border-t border-gray-100 hover:bg-gray-50/60"
                      >
                        <Td>
                          <div className="flex items-center gap-3">
                            <Avatar
                              first={_team_.firstname}
                              last={_team_.lastname}
                            />
                            <div className="min-w-0">
                              <div className="truncate font-semibold text-gray-900">
                                {_team_.firstname} {_team_.lastname}
                              </div>
                              <div className="truncate text-[11px] text-gray-500">
                                ID: {_team_.team_id}
                              </div>
                            </div>
                          </div>
                        </Td>

                        <Td className="whitespace-nowrap">{_team_.phone_number}</Td>
                        <Td className="truncate">{_team_.email_address}</Td>

                        <Td className="text-center">
                          {_team_.status === 1 ? (
                            <Badge tone="emerald">Active</Badge>
                          ) : (
                            <Badge tone="gray">Suspended</Badge>
                          )}
                        </Td>

                        <Td>
                          <div className="flex justify-center">
                            <ActionButton props={_team_} />
                          </div>
                        </Td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="px-4 py-6 text-center text-gray-500" colSpan={5}>
                        {team ? "No results." : "Loading…"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Subtle bottom accent contained inside the card (no overflow) */}
            <div className="h-1 w-full bg-gradient-to-r from-emerald-500 via-amber-500 to-fuchsia-500 opacity-70" />
          </div>

          {/* Pagination */}
          <div className="border-t border-gray-100 px-4 py-3">
            <div className="flex items-center justify-end">
              <Pagination
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                total_items={count}
                callbackFunction={(_page_, _pageSize_) => {
                  fetchTeams(_page_, _pageSize_);
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------- Subcomponents (visual only) ---------- */

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 font-semibold ${className}`}>{children}</th>
);

const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 align-middle ${className}`}>{children}</td>
);

const Badge = ({ tone = "gray", children }) => {
  const tones = {
    gray: "text-gray-700 bg-gray-100 ring-gray-200",
    emerald: "text-emerald-700 bg-emerald-50 ring-emerald-200",
    amber: "text-amber-700 bg-amber-50 ring-amber-200",
  };
  return (
    <span
      className={[
        "inline-flex items-center rounded-lg px-2.5 py-1 text-[11px] font-semibold ring-1",
        tones[tone] || tones.gray,
      ].join(" ")}
    >
      {children}
    </span>
  );
};

const StatChip = ({ label, value, tone = "slate" }) => {
  const tones = {
    slate: "from-slate-900 to-slate-700",
    emerald: "from-emerald-600 to-emerald-500",
  };
  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] text-gray-600">{label}</span>
      <span
        className={[
          "grid place-items-center rounded-md px-2.5 py-1 text-[11px] font-semibold text-white shadow",
          "bg-gradient-to-br",
          tones[tone] || tones.slate,
        ].join(" ")}
      >
        {typeof value === "number" ? value.toLocaleString() : value}
      </span>
    </div>
  );
};

const Avatar = ({ first = "", last = "" }) => {
  const text =
    (first?.[0] || "").toUpperCase() + (last?.[0] || "").toUpperCase() || "TM";
  return (
    <div className="grid h-9 w-9 place-items-center rounded-lg bg-gray-900 text-white text-xs font-bold shadow-sm">
      {text}
    </div>
  );
};

/**
 *  @author Ntavigwa Bashombe
 *  (Design only—logic unchanged)
 */
const ActionButton = ({ props }) => {
  const { team, updateTeamState, deleteTeam, updateTeam } = useTeamContext();

  const [showModal, setShowModal] = useState(false);

  const team_id = props.team_id;
  const team_name = `${props.firstname || ""} ${props.lastname || ""}`.trim() || props.name;
  const _team_ = props;

  const deleteOneTeamById = async (team_id) => {
    const response = await deleteTeam(team_id);
    if (response.data.status === 200) {
      if (team) {
        const update = team.filter((_team_) => _team_.team_id !== team_id);
        updateTeamState(update);
        setShowModal(false);
      }
    }
  };

  const suspension = async (__team__) => {
    const data = {
      email_address: __team__.email_address,
      firstname: __team__.firstname,
      lastname: __team__.lastname,
      password: "000000",
      country: __team__.country,
      city: __team__.city,
      address: __team__.address,
      phone_number: __team__.phone_number,
      role: __team__.role,
      status: __team__.status === 1 ? 0 : 1,
    };

    const response = await updateTeam(__team__.team_id, data);

    if (response.data.status === 200) {
      if (team) {
        const array = [];
        team.map((___team___) => {
          if (___team___.team_id === team_id)
            return array.push({
              team_id: __team__.team_id,
              added_by: __team__.added_by,
              createdAt: __team__.createdAt,
              updatedAt: ___team___.updatedAt,
              ...data,
            });
          else return array.push(___team___);
        });
        updateTeamState(array);
      }
    }
  };

  return (
    <>
      <Modal
        props={{
          name: team_name,
          showModal: showModal,
          setShowModal: () => setShowModal(false),
          delete: () => deleteOneTeamById(team_id),
        }}
      />

      {/* Segmented, compact action bar */}
      <div
        className={[
          "inline-flex items-center gap-2 rounded-xl bg-white/70 px-2 py-2",
          "ring-1 ring-gray-200 shadow-sm",
        ].join(" ")}
      >
        <Button
          props={{
            type: "button",
            title: _team_.status === 1 ? "Suspend" : "Unsuspend",
            category: _team_.status === 1 ? "orange" : "black",
            action: "suspend",
            onClick: () => suspension(_team_),
          }}
        />

        <Link to={editRoute}>
          <Button
            props={{
              type: "button",
              title: "Edit",
              category: "blue",
              action: "edit",
              onClick: () => {
                pushOneTeam(_team_);
              },
            }}
          />
        </Link>

        <Button
          props={{
            type: "button",
            title: "Delete",
            category: "red",
            action: "delete",
            onClick: () => {
              setShowModal(true);
            },
          }}
        />
      </div>
    </>
  );
};

export default Overview;
